// Vue
import { Ref, ref } from "vue";
import i18n from "@/services/i18n";
import type { LocaleCode } from "@/services/i18n";

// Dependencies
import { defineStore } from "pinia";

// providers
import { useLocalization } from "@/providers/Localization";

// Types
import { TranslatationByCategoryAndLanguage } from "../types/CategoryTranslationsType";

// GraphQL
import { GetCategoryTranslationsQuery } from "@/graphql";

interface CategoryTranslationsProvider {
  categoryTranslations: Ref;
  // Getters
  getAll: () => TranslatationByCategoryAndLanguage | undefined;
  getAllIds: () => string[];

  // Methods
  mapData(data: GetCategoryTranslationsQuery): void;
  translate: (categoryId: string, languageCodes?: string[]) => { languageCode?: string; value: string };

  // Utility
  getLoading: () => boolean;
  setLoading: (newValue: boolean) => void;
}

const useCategoryTranslations = defineStore("CategoryTranslationsProvider", (): CategoryTranslationsProvider => {
  // ///////////////////
  // Providers
  // ///////////////////
  const upperToLocaleMap: Record<string, string> = {
    EN: "en",
    DA: "da",
    SV: "sv",
    DE: "de",
    IS: "is",
    NO: "no",
  };

  const localization = useLocalization();

  // ///////////////////
  // Refs
  // ///////////////////

  const categoryTranslations = ref<TranslatationByCategoryAndLanguage>();

  // ///////////////////
  // Getters
  // ///////////////////

  function getAll() {
    return categoryTranslations.value;
  }

  function getAllIds() {
    return Object.keys(categoryTranslations.value || {}).map((key) => key);
  }

  // ///////////////////
  // Setters
  // ///////////////////

  // Find first matching translation from languageCodes
  function translate(categoryId: string, languageCodes?: string[]) {
    const messages = i18n.global.messages.value as any;
    const normalizedCategoryId = categoryId.replace(/\s+/g, "_").toLowerCase();
    const lCodes = languageCodes || localization.getLanguages();

    for (const languageCode of lCodes) {
      const value = messages?.[languageCode]?.category?.[normalizedCategoryId] as string;

      if (value) {
        return { languageCode, value };
      }
    }

    return { value: categoryId };
  }

  // ///////////////////
  // Methods
  // ///////////////////

  function mapData(data: GetCategoryTranslationsQuery) {
    const { translationCategory } = data;
    if (!translationCategory) return;

    const messages = i18n.global.messages.value as any;

    translationCategory.forEach(({ id, original, languageCode, translation }) => {
      const upperCode = languageCode.toUpperCase();
      const locale = upperToLocaleMap[upperCode];

      if (!locale) return;
      const typedLocale = locale as LocaleCode;

      if (!messages[typedLocale]) {
        messages[typedLocale] = {};
      }

      if (!messages[typedLocale].category) {
        messages[typedLocale].category = {};
      }

      const cleanTranslation = translation?.trim();

      const normalizedId = original.replace(/\s+/g, "_").toLowerCase();

      if (cleanTranslation) {
        messages[typedLocale].category[id] = cleanTranslation;
        messages[typedLocale].category[normalizedId] = cleanTranslation;
      }
    });

    i18n.global.setLocaleMessage(i18n.global.locale.value, messages[i18n.global.locale.value]);
  }

  // ///////////////////
  // Utility
  // ///////////////////

  const loading = ref<boolean>(true);

  function setLoading(newValue: boolean) {
    loading.value = newValue;
  }

  function getLoading() {
    return loading.value;
  }

  // ///////////////////
  // Returns
  // ///////////////////

  return {
    categoryTranslations,
    // Getters
    getAll,
    getAllIds,

    // Methods
    // fetch,
    translate,
    mapData,

    // Utility
    setLoading,
    getLoading,
  };
});

export default useCategoryTranslations;
